/**
 * Hard validation for line items at the Lines step (memory/BACKLOG-post-merge.md
 * Batch 1, pulled forward as a blocker 2026-07-21: sweep Test 1 found an
 * edited line with a blanked description sweeping into the RFP with no
 * validation anywhere on the path). Design Principle 7 (validation precedes
 * persistence): this is the client-side half - inline per-field errors and
 * a disabled Finalize button. The backend (routes/_line_item_validation.py)
 * mirrors the same rules as the authoritative backstop for a stale client.
 *
 * Consumption-side gap closed (Ashok ruling, 2026-07-21): validation now
 * gates the FULL final line-item set, not just in-scope lines -
 * final_line_items is the canonical input for RFP/risk (Principle 3), and
 * validation-precedes-persistence applies regardless of who authored the
 * data (Principle 7). isLineInScope() is kept purely to select MESSAGE
 * FRAMING, not to skip lines:
 * - in-scope (modified===true or source==='user'): user-error framing,
 *   e.g. "Description is required" - the user just did this.
 * - out-of-scope (untouched AI/BOQ line): completion framing, e.g.
 *   "Line 3 needs a delivery date" - the user didn't touch this line, so
 *   an error must read as "this needs finishing", not "you made a mistake".
 * UoM is the one field that's exempt even for out-of-scope lines - it
 * keeps the silent _apply_default_uom backstop applied server-side at
 * finalize (routes/procurement.py), so a blank uom there is never
 * surfaced as an error here either - Finalize must not be blocked for
 * something the backend will happily repair.
 *
 * Copy rule (Item 0, 2026-07-21): an error must state which line, which
 * field, why, and the value it was compared against, where one exists.
 * Every date-comparison rule (future-date vs today, before-deadline vs
 * deadline, end-after-start vs start) and the category-not-found rule
 * embed the concrete value(s) directly in the message (DD-MMM-YYYY via
 * formatDate) - mirrors routes/_line_item_validation.py's
 * _format_date_display enrichment exactly, so a server-caught (stale
 * client) error reads identically to a client-caught one.
 *
 * Item 2d (2026-07-21 sweep): uom master-existence check, in-scope lines
 * ONLY - deliberately narrower than spend_category's full-set check (which
 * fires regardless of scope). An out-of-scope line's non-blank-but-legacy/
 * variant uom (e.g. "Sqft" values that predate uom_master) is never
 * re-validated against the master - only a line the user actually touched
 * must resolve to a value uomCodes knows. Mirrors
 * routes/_line_item_validation.py's valid_uom_codes gating exactly.
 *
 * Date-validation-fixes batch (2026-07-23): date-rule evaluation (in-past,
 * end-before-start, on/before-submission) now delegates to the shared
 * dateValidationRules.checkLineItemDateRules - the ONE implementation also
 * used at Answers and the RFP-generation gate. This module's own job for
 * dates is now just: (a) the presence/valid-format check (unchanged,
 * date-agnostic to the shared rules), and (b) building the final
 * scope-aware, value-embedded message text for whichever rule code fired -
 * a presentation concern specific to Lines, not shared logic.
 */
import { formatDate } from './utils';
import { hasCategoryNoFit } from './categoryNoFit';
import {
  effectiveItemType,
  classifyLineItems,
  effectiveScheduleMode,
  toIsoDateOrNull,
  checkLineItemDateRules,
  appendRuleCodeSuffix,
  sortBySeverity,
} from './dateValidationRules';

// Moved to dateValidationRules.js (dependency-free target, date-validation-
// fixes batch) - re-exported unchanged for existing call sites
// (LineItemsEditor.jsx imports both from '@/lib/lineItemValidation').
export { effectiveItemType, classifyLineItems };

const DATE_FIELD_LABELS = {
  need_by_date: 'Need by date',
  start_date: 'Start date',
  end_date: 'End date',
};

// Completion-framing noun for each date field, used in out-of-scope
// messages ("Line N needs a {completion label}") - mirrors the backend's
// _DATE_FIELD_COMPLETION_LABELS exactly.
const DATE_FIELD_COMPLETION_LABELS = {
  need_by_date: 'delivery date',
  start_date: 'start date',
  end_date: 'end date',
};

export const isLineInScope = (item) => item?.modified === true || item?.source === 'user';

// Builds the final, scope-aware, value-embedded message for one hard-stop
// rule code (from checkLineItemDateRules) - the shared function's own
// generic `.message` is used as-is by Answers/the gate (no scope concept
// there); Lines' in-scope/out-of-scope framing + embedded values is a
// presentation concern that lives here instead. Item 2 (consolidated fix
// batch, 2026-07-24): the rule-code suffix is appended here too - this is
// an inline FieldError (not a dialog), so it's the plain-text form.
const formatDateHardStopMessage = (code, { inScope, lineNumber, item, submissionDeadline, now }) => {
  const todayDisplay = formatDate(toIsoDateOrNull(now || new Date()));
  const deadlineIso = toIsoDateOrNull(submissionDeadline);
  const deadlineDisplay = deadlineIso ? formatDate(deadlineIso) : '';

  let message = '';

  if (code === 'HVND' || code === 'HVSD') {
    const field = code === 'HVSD' ? 'start_date' : 'need_by_date';
    const label = DATE_FIELD_LABELS[field];
    const completionLabel = DATE_FIELD_COMPLETION_LABELS[field];
    const display = formatDate(toIsoDateOrNull(item[field]));
    message = inScope
      ? `${label} (${display}) must be a future date (today is ${todayDisplay})`
      : `Line ${lineNumber}'s ${completionLabel} (${display}) must be a future date (today is ${todayDisplay})`;
  } else if (code === 'HVES') {
    const startDisplay = formatDate(toIsoDateOrNull(item.start_date));
    const endDisplay = formatDate(toIsoDateOrNull(item.end_date));
    message = inScope
      ? `End date (${endDisplay}) must be on or after start date (${startDisplay})`
      : `Line ${lineNumber}'s end date (${endDisplay}) must be on or after its start date (${startDisplay})`;
  } else if (code === 'HVPN' || code === 'HVPS') {
    const field = code === 'HVPS' ? 'start_date' : 'need_by_date';
    const display = formatDate(toIsoDateOrNull(item[field]));
    if (code === 'HVPS') {
      message = inScope
        ? `Service Start Date (${display}) must be after the submission deadline (${deadlineDisplay})`
        : `Line ${lineNumber}'s start date (${display}) must be after the submission deadline (${deadlineDisplay})`;
    } else {
      message = inScope
        ? `Select Proposal Submission which is before the Need by Date (${display})`
        : `Line ${lineNumber}'s need by date (${display}) must be after the submission deadline (${deadlineDisplay})`;
    }
  } else {
    return '';
  }

  return appendRuleCodeSuffix(message, code);
};

/**
 * Returns { [field]: message } for one line - empty object if every rule
 * passes. Every line is checked regardless of scope; isLineInScope() only
 * picks which of the two message framings a given failure uses (see
 * module docstring). `index` is the 0-based position in the full line-item
 * array, used as the line-number fallback for completion-framing messages
 * when a line has no line_number of its own yet. `itemTypeOverride`, if
 * given, is used instead of re-deriving effectiveItemType(item,
 * procurementType) internally (Defect A fix, 2026-07-21) - pass the
 * corresponding entry from classifyLineItems() so this line's date rule
 * is guaranteed to match whatever the caller already decided to render.
 */
/**
 * The unit's quantity_type, or null when we do not know.
 *
 * PERMISSIVE WHEN UNKNOWN, AND THAT IS THE WHOLE DESIGN (Ashok ruling,
 * 2026-08-24). null means "no unit chosen, or a unit that does not resolve
 * to a master row" - NOS and Months are the live examples - and null never
 * blocks. We do not know, so we do not refuse.
 *
 * DELIBERATELY NOT statedIntentValidationRules.js's findUomSemantics, and
 * DO NOT "ALIGN" THE TWO. That helper does the same name-keyed find over
 * rows the surface already holds - this follows its SHAPE on purpose - but
 * it defaults a MISSING row to 'procurement_object', which is a deliberate
 * fail-safe-to-FIRE for SARU: an unmapped unit there SHOULD raise an echo.
 * Here the fail-safe runs the other way. Reusing that default would
 * silently convert "unknown" into a decision and refuse a quantity on a
 * unit nobody has classified - the exact over-reach this rule must not
 * commit.
 *
 * Twin of routes/_line_item_validation.uom_quantity_type. Touch one,
 * verify the other (CLAUDE.md mirror-pair registry).
 */
export const uomQuantityType = (uom, uomQuantityTypes) => {
  if (!uomQuantityTypes) return null;
  const name = uom ? String(uom).trim() : '';
  if (!name) return null;
  const get = uomQuantityTypes instanceof Map
    ? uomQuantityTypes.get(name)
    : uomQuantityTypes[name];
  const value = get ? String(get).trim().toLowerCase() : '';
  return value || null;
};

// THE SHIP-TO RULE (2026-09-16) - the client twin of
// backend/routes/_ship_to_canonicalization.canonicalize_line_ship_to, which
// finalize_line_items applies to every line against the ACTIVE location
// master. It exists because an unmatched ship-to used to be informational and
// now BLOCKS: the server refuses the save (422) and the master-reference guard
// refuses the next forward boundary, while the Lines cell showed a bare code
// and no error until a save was attempted (PROC-000353).
//
// ONE RULE, WRITTEN TWICE - A REGISTERED MIRROR PAIR (CLAUDE.md, "Line-item
// hard validation"), pinned by a shared case table,
// backend/tests/fixtures/ship_to_line_rule_parity_cases.json, which BOTH
// backend/tests/test_ship_to_line_rule_parity.py and
// lib/lineItemValidation.shipTo.parity.test.js assert. The two sides agree on:
//   - a blank value (null, "", whitespace) is legitimate - no error
//   - normalisation: trimmed and upper-cased, on BOTH the value and the codes
//   - shape: [A-Z]{2,4} then three digits, whole string (the server's
//     agents._LOCATION_CODE_PATTERN, applied with fullmatch)
//   - membership in the ACTIVE code set
//   - the MESSAGE, word for word, quoting the value as stored
// ONE DELIBERATE DIFFERENCE, not in the table: an EMPTY code set. The server
// refuses (503 - "we could not check" must not authorise a write); here the
// rule SKIPS, the same convention as categoryCodes and uomCodes while the
// list is still loading, so every line does not flash an error on mount.
//
// NOT scope-gated, matching the server, which checks every line.
export const LOCATION_CODE_SHAPE = /^[A-Z]{2,4}\d{3}$/;

// BY REASON (2026-09-16). A RETIRED code - one the master holds with
// is_active false - is never called "not valid": that reads as a wrong code,
// and the buyer's code was withdrawn, not mistyped (15ce599 split the header
// copy for the same reason). `retiredLocationCodes` comes from the SAME fetch
// as `locationCodes` (LineItemsEditor splits one GET /master/locations), so a
// code can never be both, or neither when present. Shape is checked FIRST,
// exactly as the server does, so a malformed value keeps the existing wording.
export const shipToLocationError = (value, locationCodes, retiredLocationCodes = null) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text) return null;
  if (!locationCodes || locationCodes.size === 0) return null;
  const normalise = (codes) => new Set([...(codes || [])].map((c) => String(c).trim().toUpperCase()));
  const active = normalise(locationCodes);
  const code = text.toUpperCase();
  if (LOCATION_CODE_SHAPE.test(code) && active.has(code)) return null;
  if (LOCATION_CODE_SHAPE.test(code) && normalise(retiredLocationCodes).has(code)) {
    return `'${value}' is no longer an active ship-to location. Choose a location from the list.`;
  }
  return `'${value}' is not a valid ship-to location. Choose a location from the list.`;
};

export const validateLineItem = (
  item,
  { procurementType, submissionDeadline, categoryCodes, retiredCategoryCodes, uomCodes, uomQuantityTypes, locationCodes, retiredLocationCodes, now } = {},
  index = 0,
  itemTypeOverride = null
) => {
  const errors = {};
  const inScope = isLineInScope(item);
  const lineNumber = item?.line_number ?? index + 1;

  if (!item.description || !String(item.description).trim()) {
    errors.description = inScope ? 'Description is required' : `Line ${lineNumber} needs a description`;
  }

  const quantity = item.quantity;
  if (quantity === null || quantity === undefined || quantity === '' || Number.isNaN(Number(quantity)) || Number(quantity) <= 0) {
    errors.quantity = inScope ? 'Quantity must be greater than 0' : `Line ${lineNumber} needs a quantity greater than 0`;
  } else if (
    // A FRACTIONAL QUANTITY ON A WHOLE-ONLY UNIT (Ashok ruling,
    // 2026-08-24). "0.2 Each" reached a vendor schedule on PROC-000362.
    //
    // NOT inScope-gated, same as the greater-than-zero branch above and
    // for the same reason: the case this exists for is a BOQ-derived line
    // the buyer never touched, which is OUT of scope. Gating it the way
    // the uom-existence check below is gated would miss exactly that line.
    //
    // Permissive when unknown - uomQuantityType returns null for a blank
    // or unmastered unit and null falls through. NO REPAIR: nothing
    // rounds, floors or coerces; the value stays as the buyer wrote it.
    uomQuantityType(item.uom, uomQuantityTypes) === 'integer'
    && Number(quantity) % 1 !== 0
  ) {
    const unitName = String(item.uom).trim();
    errors.quantity = inScope
      ? `Quantity must be a whole number for ${unitName}`
      : `Line ${lineNumber} needs a whole-number quantity for ${unitName}`;
  }

  // Out-of-scope (untouched AI/BOQ) lines keep the silent _apply_default_uom
  // backstop applied server-side at finalize - a blank uom there is never
  // surfaced as an error here, so Finalize isn't blocked for something the
  // backend will happily repair. In-scope lines get no repair - a blank
  // uom on a user-touched line still hard-fails, same as every other rule.
  const uom = item.uom ? String(item.uom).trim() : '';
  if (inScope && !uom) {
    errors.uom = 'UoM is required';
  } else if (inScope && uom && uomCodes && uomCodes.size > 0 && !uomCodes.has(uom)) {
    errors.uom = `UoM '${uom}' not found in master data`;
  }

  // THE SAVE-ARM EXEMPTION (F1 / C1b-1b, A3a - Ashok ruling).
  //
  // A no-fit line carries NO category by construction - that is what the
  // declaration means - so without this it would fail "Category is
  // required" before the save, and the buyer's request would never reach
  // the admin at all. TWO ARMS AT THE GATE: a no-fit line SAVES, because
  // the declaration is buyer-authored content that must be persisted, and
  // is REFUSED FORWARD at generation, which is where new commitment is
  // created. This is the save arm.
  //
  // IT IS NOT A BLANK-CATEGORY EXEMPTION, and the distinction is the whole
  // risk. An ordinary line the buyer simply has not categorised yet is
  // STILL refused, exactly as before - the exemption keys on a valid
  // DECLARATION read through the shared reader (a mirror pair with
  // read_category_no_fit; see CLAUDE.md), never on the absence of a
  // category. A half-record - a declaration with no text, or text with no
  // declaration - reads as absent and the line is refused, which is
  // correct: that request could tell the admin nothing.
  //
  // BOTH SIDES OF THIS PAIR AND THE SHARED TABLE MOVE TOGETHER. See
  // backend/tests/fixtures/category_no_fit_exemption_parity_cases.json.
  const spendCategory = item.spend_category ? String(item.spend_category).trim() : '';
  const declaredNoFit = hasCategoryNoFit(item);
  if (declaredNoFit) {
    // A line cannot both carry a category and say that none of them fit.
    // The picker clears the category when the declaration is made and
    // deletes the declaration when a category is chosen, so this state is
    // unreachable through the UI - but a hand-built payload can express
    // it, and silently preferring one half would be an inference.
    if (spendCategory) {
      errors.spend_category = inScope
        ? 'This line cannot carry both a category and a no-fit request'
        : `Line ${lineNumber} carries both a category and a no-fit request`;
    }
  } else if (!spendCategory) {
    errors.spend_category = inScope ? 'Category is required' : `Line ${lineNumber} needs a spend category`;
  } else if (categoryCodes && categoryCodes.size > 0 && !categoryCodes.has(spendCategory)) {
    // BY REASON (2026-09-16). A RETIRED category (in the master, inactive)
    // says so; absent and malformed keep the existing wording.
    //
    // THE CATEGORY PAIR DIVERGES HERE ON PURPOSE - declared in
    // backend/tests/fixtures/line_category_reference_parity_cases.json. This
    // client check refuses a retired code because the Lines step's Generate
    // RFP is a FORWARD transition (finalize, then generate). The server's
    // SAVE (_line_item_validation.py, fed ALL rows by finalize_line_items)
    // accepts it, per the 2026-08-15 ruling that a save must not fail on a
    // retirement (R1). Both are correct. Do not "align" either side.
    if (retiredCategoryCodes && retiredCategoryCodes.has(spendCategory)) {
      errors.spend_category = inScope
        ? `Category '${spendCategory}' is no longer active - please choose a current category`
        : `Line ${lineNumber}'s spend category ('${spendCategory}') is no longer active - please update it`;
    } else {
      errors.spend_category = inScope
        ? `Category '${spendCategory}' not found in master data`
        : `Line ${lineNumber}'s spend category ('${spendCategory}') is not recognized - please update it`;
    }
  }

  const shipToError = shipToLocationError(item.ship_to_location, locationCodes, retiredLocationCodes);
  if (shipToError) {
    errors.ship_to_location = shipToError;
  }

  // Presence/valid-format check only - date-agnostic to the shared rules
  // (a missing/unparseable date can't be rule-evaluated at all; this stays
  // local since it's a Lines-specific completeness concern, same as
  // description/quantity/category above).
  const checkPresence = (field) => {
    const label = DATE_FIELD_LABELS[field];
    const completionLabel = DATE_FIELD_COMPLETION_LABELS[field];
    const article = /^[aeiou]/i.test(completionLabel) ? 'an' : 'a';
    const raw = item[field];
    if (!raw) {
      errors[field] = inScope ? `${label} is required` : `Line ${lineNumber} needs ${article} ${completionLabel}`;
      return false;
    }
    if (!toIsoDateOrNull(raw)) {
      errors[field] = inScope ? `${label} is not a valid date` : `Line ${lineNumber}'s ${completionLabel} is not a valid date`;
      return false;
    }
    return true;
  };

  const checkScheduleNoteRequired = () => {
    if (!item.schedule_note || !String(item.schedule_note).trim()) {
      errors.schedule_note = inScope
        ? 'Schedule note is required for a vendor-proposed schedule'
        : `Line ${lineNumber} needs a schedule note for its vendor-proposed schedule`;
    }
  };

  const itemType = itemTypeOverride || effectiveItemType(item, procurementType);
  const scheduleMode = effectiveScheduleMode(item);

  // EXECUTION SCHEDULE MODEL (Ashok's design, 2026-07-26; generalized to
  // GOODS lines by the MIXED-SEMANTICS + ECHO FIX BATCH, item C - "need-by
  // is requirement-level in mixed": a goods line with no execution
  // relationship explicitly resolved is left schedule_mode=VENDOR_PROPOSED
  // too, same rule set as services). Pure-goods procurements are UNCHANGED
  // in practice - nothing ever sets schedule_mode on a goods line there, so
  // effectiveScheduleMode always resolves BUYER_DEFINED and this branch is
  // never reached.
  if (itemType === 'goods') {
    if (scheduleMode === 'VENDOR_PROPOSED') {
      // Absence of a date is the RULE, not merely an allowance - reject if
      // present (defensive; LineItemsEditor's own updateLineItem
      // auto-flips schedule_mode to BUYER_DEFINED the instant the user
      // types a date, so this combination shouldn't arise from normal
      // editing).
      if (item.need_by_date) {
        errors.need_by_date = inScope
          ? 'Need by date must not be set for a vendor-proposed schedule'
          : `Line ${lineNumber}'s delivery date must be cleared, or switch this line to buyer-defined dates`;
      }
      checkScheduleNoteRequired();
    } else {
      checkPresence('need_by_date');
    }
  } else if (scheduleMode === 'VENDOR_PROPOSED') {
    if (item.start_date) {
      errors.start_date = inScope
        ? 'Start date must not be set for a vendor-proposed schedule'
        : `Line ${lineNumber}'s start date must be cleared, or switch this line to buyer-defined dates`;
    }
    if (item.end_date) {
      errors.end_date = inScope
        ? 'End date must not be set for a vendor-proposed schedule'
        : `Line ${lineNumber}'s end date must be cleared, or switch this line to buyer-defined dates`;
    }
    checkScheduleNoteRequired();
  } else {
    checkPresence('start_date');
    checkPresence('end_date');
  }

  // Rule-based hard stops (in-past, end-before-start, on/before-submission)
  // - the shared function silently no-ops on a field that failed presence/
  // format above (it treats an unparseable date as absent), so calling it
  // unconditionally here never double-errors or overwrites the messages
  // just set.
  const { hardStops } = checkLineItemDateRules(item, { submissionDeadline, itemTypeOverride: itemType, now });
  hardStops.forEach((stop) => {
    if (errors[stop.field]) return;
    errors[stop.field] = formatDateHardStopMessage(stop.code, { inScope, lineNumber, item, submissionDeadline, now });
  });

  return errors;
};

// Array of { [field]: message }, one entry per line, index-aligned with
// lineItems (matches the backend's line_index convention). `itemTypes`, if
// given, must be index-aligned with lineItems (i.e. classifyLineItems'
// output) - passed through per-line so validation never re-derives its
// own classification independently of the caller's.
export const validateLineItems = (lineItems, opts, itemTypes = null) =>
  (lineItems || []).map((item, index) =>
    validateLineItem(item, opts, index, itemTypes ? itemTypes[index] : null)
  );

// Date-validation-fixes batch (2026-07-23), items 3/4: per-line SOFT
// alerts (SANT/SAST/SAPN/SAPS - need-by/start close to today or to
// submission), consolidated into one sentence per affected line - used by
// LineItemsEditor's amber status column + StepLineItems' Generate-click
// summary dialog. Submission-vs-today (SAPT) is NOT included here - it's
// procurement-wide, not line-specific, and is surfaced once near the
// Lines-step submission reference box instead (see StepLineItems.jsx).
//
// Item 2 (consolidated fix batch, 2026-07-24): `consolidatedMessage` is the
// PLAIN-text form (rule codes combined into one trailing bracket, e.g.
// "[SANT, SAPN]") - used by LineItemsEditor's amber hover/pin panel, which
// is inline table content, not a Dialog. The Generate-click summary dialog
// builds its OWN muted-styled rendering directly from `alerts` instead (a
// dialog needs the code suffix visually separated, not plain-concatenated).
export const computeLineSoftAlerts = (lineItems, opts, itemTypes = null) =>
  (lineItems || [])
    .map((item, index) => {
      const itemType = itemTypes ? itemTypes[index] : null;
      const { softAlerts } = checkLineItemDateRules(item, { ...opts, itemTypeOverride: itemType });
      // Bug fix (found while adding item 2's suffix tests): checkDateRules
      // always evaluates SAPT alongside SANT/SAPN for any call that passes
      // a submission value - it was never actually being filtered out here
      // despite this function's own doc comment claiming it was, so it was
      // silently duplicating StepLineItems' own separate submission-only
      // SAPT check into every line's consolidated message.
      // SOFT-ALERT SEVERITY BATCH (2026-07-25): sorted most-severe-first
      // (SANT/SAST HIGH before SAPN/SAPS MEDIUM) so every downstream
      // consumer - consolidatedMessage's code order, the amber hover/pin
      // panel, the Generate summary dialog - lists/reads codes in severity
      // order without each having to re-sort independently.
      const alerts = sortBySeverity(softAlerts.filter((a) => a.code !== 'SAPT'));
      return { lineIndex: index, alerts };
    })
    .filter((entry) => entry.alerts.length > 0)
    .map((entry) => ({
      ...entry,
      // Generate-summary polish (2026-07-27): each contributing rule's
      // sentence starts on its own line - joined with '\n', not a space -
      // so a two-alert line doesn't read as one run-on paragraph. Codes
      // stay combined in ONE trailing bracket at the very end (unchanged).
      // Rendering this requires `white-space: pre-line` at the consuming
      // surface (LineItemsEditor's hover/pin panel) for the '\n' to
      // actually break visually.
      consolidatedMessage: appendRuleCodeSuffix(
        entry.alerts.map((a) => a.message).join('\n'),
        entry.alerts.map((a) => a.code)
      ),
    }));

const FIELD_ORDER = ['description', 'quantity', 'uom', 'spend_category', 'need_by_date', 'start_date', 'end_date'];

// Enumerated, one row per error - replaces the bare "Fix N line item
// issue(s)" counter under Finalize (Item 0, 2026-07-21: a count with no
// visible per-row highlight left the user unable to tell what to fix).
// Reuses the already scope-framed, already value-enriched message text
// as-is; only prepends "Line N — " when the message doesn't already open
// with its own "Line N" (out-of-scope completion messages already do,
// in-scope messages don't).
export const buildLineItemErrorSummary = (lineItems, errorsArray) => {
  const rows = [];
  (errorsArray || []).forEach((errors, index) => {
    if (!errors) return;
    const item = (lineItems && lineItems[index]) || {};
    const lineNumber = item.line_number ?? index + 1;
    const fields = Object.keys(errors).sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b));
    fields.forEach((field) => {
      const message = errors[field];
      if (!message) return;
      rows.push(message.startsWith('Line ') ? message : `Line ${lineNumber} — ${message}`);
    });
  });
  return rows;
};

export const hasAnyLineErrors = (errorsArray) => (errorsArray || []).some((e) => Object.keys(e || {}).length > 0);

// LINES, not errors: a line with a blank quantity and a retired code is one
// line the buyer visits (the pointer beside Generate RFP, 2026-09-17).
export const countLinesWithErrors = (errorsArray) => (errorsArray || []).filter((e) => Object.keys(e || {}).length > 0).length;

// "3 lines need attention - see the highlighted fields above." Points at the
// inline field errors, which is where every line error renders; it is not a
// second list of them (Ashok ruling, 2026-09-17).
export const describeLineErrorPointer = (count) => (
  `${count} ${count === 1 ? 'line needs' : 'lines need'} attention - see the highlighted fields above.`
);

export const countLineErrors = (errorsArray) =>
  (errorsArray || []).reduce((sum, e) => sum + Object.keys(e || {}).length, 0);
